import { saveAs } from "file-saver";

// A filled day sheet with a real dropdown on chosen columns.
//
// The blank templates and the other filled sheets are written with SheetJS
// (`writeWorkbook` in dailyUploadShared), which cannot write data validation —
// so a sheet that needs a picker is written with exceljs instead. exceljs is
// already a dependency and is loaded the way the report exporters load it:
// `await import(...)`, so it stays in its own async chunk and never reaches the
// main bundle.

export interface DropdownSpec {
  /** 1-based column index the dropdown applies to. */
  column: number;
  /** The values the cell accepts. Written to a hidden sheet and referenced. */
  values: string[];
  errorTitle?: string;
  error?: string;
}

export interface ValidatedSheetSpec {
  name: string;
  headers: string[];
  widths: number[];
  rows: any[][];
  dropdowns?: DropdownSpec[];
  /**
   * 1-based columns the user actually fills in. They are tinted the whole way
   * down so the sheet answers "what do I type?" before anyone opens the
   * Instructions tab; every other column is reference.
   */
  inputColumns?: number[];
  /**
   * Individual cells INSIDE an input column that are reference after all, as
   * [row, column] with row 1 being the header. They are painted ref-grey so a
   * column that is only sometimes writable does not invite an entry that would
   * be rejected — the Store-Stock sheet's Opening columns are editable on a
   * material's first day and carried (read-only) on every day after it.
   */
  refCells?: Array<[number, number]>;
  /**
   * Excel number formats by 1-based column, e.g. { 12: '#,##0.00' }. Applied to
   * the data rows only, so a computed column reads as money or a percentage
   * instead of a raw float. Columns left out keep Excel's General.
   */
  numFmts?: Record<number, string>;
}

const LIST_SHEET = "Lists";
// Amber for the columns to fill, grey for everything that is already answered.
const INPUT_HEADER = "FFFFD666";
const INPUT_CELL = "FFFFFBE6";
const REF_HEADER = "FFF0F2F5";
// A cell inside an input column that is reference on this row only.
const REF_CELL = "FFF7F8FA";
const BORDER = "FFD9D9D9";
const fill = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });

export const writeValidatedWorkbook = async (
  fileName: string,
  sheet: ValidatedSheetSpec,
  instructions: string[][]
): Promise<void> => {
  // exceljs is CJS; depending on the bundler the API lands on the namespace
  // itself or under .default — handle both (same as the report exporters).
  const excelModule: any = await import("exceljs");
  const ExcelJS: typeof import("exceljs") = excelModule.Workbook ? excelModule : excelModule.default;
  const wb = new ExcelJS.Workbook();
  wb.created = new Date();

  const ws = wb.addWorksheet(sheet.name);
  ws.columns = sheet.headers.map((header, index) => ({
    header,
    width: sheet.widths[index] ?? 16,
  }));
  ws.getRow(1).font = { bold: true };
  ws.getRow(1).alignment = { vertical: "middle", wrapText: true };
  sheet.rows.forEach((row) => ws.addRow(row));
  // The header stays put while the clerk works down a long day.
  ws.views = [{ state: "frozen", ySplit: 1 }];

  // Number formats first, so an input column's tint paints over nothing.
  for (const [column, fmt] of Object.entries(sheet.numFmts || {})) {
    for (let row = 2; row <= sheet.rows.length + 1; row += 1) {
      ws.getCell(row, Number(column)).numFmt = fmt;
    }
  }

  const inputs = new Set(sheet.inputColumns || []);
  const refs = new Set((sheet.refCells || []).map(([row, column]) => `${row}:${column}`));
  const lastRow = sheet.rows.length + 1;
  sheet.headers.forEach((_, index) => {
    const column = index + 1;
    const isInput = inputs.has(column);
    const header = ws.getCell(1, column);
    header.fill = fill(isInput ? INPUT_HEADER : REF_HEADER);
    header.border = { bottom: { style: "thin", color: { argb: BORDER } } };
    header.font = { bold: true, color: { argb: isInput ? "FF613400" : "FF526484" } };
    if (!isInput) return;
    for (let row = 2; row <= lastRow; row += 1) {
      const cell = ws.getCell(row, column);
      cell.fill = fill(refs.has(`${row}:${column}`) ? REF_CELL : INPUT_CELL);
      cell.border = {
        left: { style: "thin", color: { argb: BORDER } },
        right: { style: "thin", color: { argb: BORDER } },
      };
    }
  });

  // Every value the pickers offer goes on one hidden sheet: an inline list is
  // capped at 255 characters, which a unit master blows past immediately.
  const dropdowns = (sheet.dropdowns || []).filter((d) => d.values.length);
  if (dropdowns.length) {
    const lists = wb.addWorksheet(LIST_SHEET);
    lists.state = "hidden";
    dropdowns.forEach((dropdown, index) => {
      const column = lists.getColumn(index + 1);
      column.values = dropdown.values;
      const ref = `${LIST_SHEET}!$${column.letter}$1:$${column.letter}$${dropdown.values.length}`;
      // Row 1 is the header, so validation starts at row 2.
      for (let row = 2; row <= sheet.rows.length + 1; row += 1) {
        ws.getCell(row, dropdown.column).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [ref],
          showErrorMessage: true,
          errorStyle: "warning",
          errorTitle: dropdown.errorTitle || "Not in the list",
          error: dropdown.error || "Pick one of the listed values.",
        };
      }
    });
  }

  const help = wb.addWorksheet("Instructions");
  help.columns = [{ width: 26 }, { width: 120 }];
  instructions.forEach((line) => help.addRow(line));
  help.getRow(1).font = { bold: true };

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName
  );
};
